/**
 * OrderFormPage — 주문 등록/수정 (최복잡 마스터-디테일 + 서브 아이템).
 *
 * 【역할】
 * - 주문 마스터 + 상세 품목 + 하위 아이템 3-depth 저장 샘플
 * - URL ?id=xxx 유무로 신규/수정 모드 판별
 *
 * 【주요 기능】
 * - Tab 1: 마스터 폼 (고객/일자/상태/결제수단)
 * - Tab 2: 상세 품목 인라인 편집 그리드 (추가/삭제/수량·단가 cascading)
 * - Tab 3: 선택 행의 하위 아이템 (읽기 전용)
 * - 저장: 3 데이터셋(master/detail/item) 을 단일 트랜잭션으로 전송
 *
 * 【사용 훅】
 * - useFramework / useDataSet
 * - useLabel / useCodes
 * - useNavigate / useSearchParams / useParams (라우팅)
 *
 * 【Backend RPC serviceName】
 * - order/saveMasterDetail  (action=save) — 3 데이터셋 원자적 저장
 *
 * 【DataSet 구조】
 * - masterDs: 주문 마스터 (단건, row[0] 사용)
 * - detailDs: 주문 상세 (N건, C/U/D 추적)
 * - itemDs:   선택된 detail 행의 하위 아이템 (N건, 읽기 전용)
 *
 * 【사용처】 router/index.tsx — `{ path: 'order/form' }` + `{ path: 'order/form/:id' }`
 * 【포팅 원본】 vue-spring-fw/frontend/src/pages/PageOrderForm.vue
 */
import { useEffect, useMemo, useState } from 'react'
import { Form, Input, Select, InputNumber, Button, Table, Card, Tabs, Space } from 'antd'
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate, useSearchParams, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import CrudToolbar from '@/components/common/CrudToolbar'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'
import { useFramework, useDataSet } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import { useCodes } from '@/framework/useCodes'
import RowStateTag from '@/components/common/RowStateTag'

interface OrderMaster {
  orderId?: string
  orderDate: string
  customerId: string
  customerName: string
  orderStatus: string
  paymentMethod: string
  totalAmount: number
  deliveryAddress: string
  remark: string
}

interface OrderDetail {
  lineNo: number
  productId: string
  productName: string
  unit: string
  qty: number
  unitPrice: number
  amount: number
  remark: string
}

interface OrderDetailItem {
  itemNo: number
  lineNo: number
  itemName: string
  itemSpec: string
  itemQty: number
}

function genOrderId(): string {
  const d = dayjs().format('YYYYMMDD')
  const seq = String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')
  return `ORD-${d}-${seq}`
}

/**
 * OrderFormPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function OrderFormPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const params = useParams<{ id?: string }>()
  // 패스 파라미터 우선, 없으면 query 파라미터로 fallback (원본 vue-spring-fw 패턴 + 기존 query 호환)
  const orderId = params.id ?? searchParams.get('id')
  const isEdit = !!orderId

  const { $transaction, $msg, loading } = useFramework()
  const { t } = useLabel()
  const { getOptions, loadCodes } = useCodes()
  const [masterForm] = Form.useForm<OrderMaster>()

  useEffect(() => {
    loadCodes(['ORDER_STATUS', 'PAYMENT_METHOD'])
  }, [loadCodes])

  const STATUS_OPTIONS = useMemo(() => getOptions('ORDER_STATUS'), [getOptions])
  const PAYMENT_OPTIONS = useMemo(() => getOptions('PAYMENT_METHOD'), [getOptions])

  // 3 datasets — 각각 useDataSet 인스턴스
  const masterDs = useDataSet<OrderMaster>([])
  const detailDs = useDataSet<OrderDetail>([])
  const itemDs = useDataSet<OrderDetailItem>([])

  const [selectedDetailKey, setSelectedDetailKey] = useState<string | null>(null)

  // ── 초기화 ──
  useEffect(() => {
    if (isEdit) {
      void fn_load()
    } else {
      initNewOrder()
    }
  }, [orderId])

  const initNewOrder = () => {
    const empty: OrderMaster = {
      orderId: genOrderId(),
      orderDate: dayjs().format('YYYY-MM-DD'),
      customerId: '',
      customerName: '',
      orderStatus: 'ORDER',
      paymentMethod: 'BANK',
      totalAmount: 0,
      deliveryAddress: '',
      remark: ''
    }
    masterDs.load([empty])
    masterForm.setFieldsValue(empty)
    detailDs.clear()
    itemDs.clear()
    setSelectedDetailKey(null)
  }

  // ── 단건 조회 ──
  const fn_load = async () => {
    const res = await $transaction({
      id: 'select',
      service: 'order',
      action: 'detail',
      inds: { ds_search: { orderId } },
      outds: {
        ds_master: masterDs,
        ds_detail: detailDs,
        ds_detail_item: itemDs
      }
    })
    if (res.success) {
      const m = masterDs.rows[0]?.data
      if (m) masterForm.setFieldsValue(m)
    }
  }

  // ── Detail 조작 ──
  const fn_addDetail = () => {
    const lineNo = detailDs.getVisibleRows().length + 1
    detailDs.addRow({
      lineNo,
      productId: '',
      productName: '',
      unit: 'EA',
      qty: 1,
      unitPrice: 0,
      amount: 0,
      remark: ''
    })
  }

  const fn_deleteDetail = () => {
    if (detailDs.selectedIndex < 0) {
      $msg.byId('MSG_SELECT_DELETE_ROW')
      return
    }
    detailDs.deleteRow(detailDs.selectedIndex)
  }

  const fn_updateDetailCell = (index: number, field: keyof OrderDetail, value: unknown) => {
    const row = detailDs.rows[index]
    if (!row) return
    const patch: Partial<OrderDetail> = { [field]: value } as Partial<OrderDetail>
    if (field === 'qty' || field === 'unitPrice') {
      const qty = field === 'qty' ? Number(value) : row.data.qty || 0
      const unitPrice = field === 'unitPrice' ? Number(value) : row.data.unitPrice || 0
      patch.amount = qty * unitPrice
    }
    detailDs.updateRow(index, patch)
  }

  // ── 마스터 totalAmount 자동 합계 ──
  const calcTotal = useMemo(
    () => detailDs.getVisibleRows().reduce((s, r) => s + (r.data.amount || 0), 0),
    [detailDs.rows]
  )
  useEffect(() => {
    if (masterDs.rows[0]) {
      masterDs.updateRow(0, { totalAmount: calcTotal })
      masterForm.setFieldValue('totalAmount', calcTotal)
    }
  }, [calcTotal])

  // ── 저장: 3 데이터셋을 한 번의 트랜잭션에 포함 ──
  const fn_save = async () => {
    const values = await masterForm.validateFields().catch(() => null)
    if (!values) return
    if (!values.customerId && !values.customerName) {
      $msg.byId('MSG_CUSTOMER_REQUIRED')
      return
    }
    if (detailDs.getVisibleRows().length === 0) {
      $msg.warn('주문 상세를 1건 이상 추가하세요.')
      return
    }
    // 마스터 row 동기화
    masterDs.updateRow(0, { ...values, totalAmount: calcTotal })

    const res = await $transaction({
      id: 'save',
      service: 'order/saveMasterDetail',
      action: 'save',
      inds: {
        ds_master: masterDs,
        ds_detail: detailDs,
        ds_detail_item: itemDs
      }
    })
    if (res.success) {
      $msg.byId('MSG_SAVE_SUCCESS')
      void fn_load()
    }
  }

  const fn_delete = () => {
    $msg.confirm('정말 삭제하시겠습니까?', async () => {
      // 마스터 1건을 삭제 상태로 전송
      if (masterDs.rows[0]) masterDs.deleteRow(0)
      const res = await $transaction({
        id: 'delete',
        service: 'order/saveMasterDetail',
        action: 'save',
        inds: { ds_master: masterDs }
      })
      if (res.success) {
        $msg.byId('MSG_DELETE_SUCCESS')
        navigate('/sales/order')
      }
    })
  }

  // ── Detail 선택 시 하위 item 표시 (로컬 필터) ──
  const onDetailSelect = (rowKey: string, index: number) => {
    setSelectedDetailKey(rowKey)
    detailDs.setSelectedIndex(index)
    const lineNo = detailDs.rows[index]?.data.lineNo
    // 실제로는 서버 조회이지만, 여기서는 itemDs 내에서 필터 표시용 selected state 사용
    // (itemDs 자체는 저장 대상)
    void lineNo
  }

  const selectedLineNo = detailDs.selectedRow?.data.lineNo
  const visibleItems = itemDs
    .getVisibleRows()
    .filter((r) => !selectedLineNo || r.data.lineNo === selectedLineNo)

  const detailDataSource = detailDs.getVisibleRows().map((r, i) => ({
    ...r.data,
    key: r._rowId,
    _index: detailDs.rows.indexOf(r),
    _state: r._state,
    _display: i + 1
  }))

  return (
    <PageContainer>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8
        }}
      >
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/sales/order')}>
            {t('BTN_LIST', '목록')}
          </Button>
          <span style={{ fontWeight: 600, marginLeft: 8, whiteSpace: 'nowrap' }}>
            {isEdit ? '주문 수정' : '주문 등록'}
          </span>
        </Space>
        <CrudToolbar
          onSearch={isEdit ? fn_load : undefined}
          onSave={fn_save}
          onDelete={isEdit ? fn_delete : undefined}
          dirty={masterDs.isDirty || detailDs.isDirty || itemDs.isDirty}
          loading={loading}
        />
      </div>

      <Card size="small" title={t('LBL_ORDER_INFO', '주문 정보')} style={{ marginBottom: 12 }}>
        <Form form={masterForm} layout="vertical" initialValues={{ totalAmount: 0 }}>
          <Space wrap>
            <Form.Item name="orderId" label={t('LBL_ORDER_NO', '주문번호')}>
              <Input disabled style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="orderDate" label={t('LBL_ORDER_DATE', '주문일')}>
              <Input type="date" style={{ width: 160 }} />
            </Form.Item>
            <Form.Item
              name="customerName"
              label={t('LBL_CUSTOMER_NAME', '고객명')}
              rules={[
                { required: true, message: t('MSG_CUSTOMER_REQUIRED', '고객을 선택하세요.') }
              ]}
            >
              <Input style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="customerId" label={t('COL_CUSTOMER_ID', '고객ID')}>
              <Input style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="orderStatus" label={t('LBL_ORDER_STATUS', '상태')}>
              <Select options={STATUS_OPTIONS} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="paymentMethod" label={t('LBL_PAYMENT_METHOD', '결제')}>
              <Select options={PAYMENT_OPTIONS} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="totalAmount" label={t('LBL_TOTAL_AMOUNT', '총 금액')}>
              <InputNumber disabled style={{ width: 180 }} formatter={(v) => `₩ ${v}`} />
            </Form.Item>
          </Space>
          <Form.Item name="deliveryAddress" label={t('LBL_DELIVERY_ADDRESS', '배송주소')}>
            <Input />
          </Form.Item>
          <Form.Item name="remark" label={t('COL_REMARK', '비고')}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Card>

      <Tabs
        items={[
          {
            key: 'detail',
            label: '주문 상세',
            children: (
              <>
                <Space style={{ marginBottom: 8 }}>
                  <Button icon={<PlusOutlined />} size="small" onClick={fn_addDetail}>
                    {t('BTN_ADD_ROW', '행 추가')}
                  </Button>
                  <Button icon={<DeleteOutlined />} size="small" danger onClick={fn_deleteDetail}>
                    {t('BTN_DELETE_ROW', '행 삭제')}
                  </Button>
                </Space>
                <DataTable
                  autoHeight={false}
                  rowKey="key"
                  dataSource={detailDataSource}
                  pagination={false}
                  rowSelection={{
                    type: 'radio',
                    selectedRowKeys: selectedDetailKey ? [selectedDetailKey] : [],
                    onChange: (keys, rows) => {
                      const key = keys[0] as string
                      const rec = rows[0] as (OrderDetail & { _index: number }) | undefined
                      if (rec) onDetailSelect(key, rec._index)
                    }
                  }}
                  columns={[
                    { title: t('LBL_LINE_NO_SHORT', '#'), dataIndex: '_display', width: 50 },
                    {
                      title: t('COL_PRODUCT_ID', '상품코드'),
                      dataIndex: 'productId',
                      width: 120,
                      render: (v, rec) => (
                        <Input
                          size="small"
                          value={v}
                          onChange={(e) =>
                            fn_updateDetailCell(rec._index, 'productId', e.target.value)
                          }
                        />
                      )
                    },
                    {
                      title: t('COL_PRODUCT_NAME', '상품명'),
                      dataIndex: 'productName',
                      width: 200,
                      render: (v, rec) => (
                        <Input
                          size="small"
                          value={v}
                          onChange={(e) =>
                            fn_updateDetailCell(rec._index, 'productName', e.target.value)
                          }
                        />
                      )
                    },
                    { title: t('COL_UNIT', '단위'), dataIndex: 'unit', width: 80 },
                    {
                      title: t('COL_QTY', '수량'),
                      dataIndex: 'qty',
                      width: 100,
                      render: (v, rec) => (
                        <InputNumber
                          size="small"
                          min={1}
                          value={v}
                          onChange={(val) => fn_updateDetailCell(rec._index, 'qty', val ?? 0)}
                        />
                      )
                    },
                    {
                      title: t('COL_UNIT_PRICE', '단가'),
                      dataIndex: 'unitPrice',
                      width: 120,
                      render: (v, rec) => (
                        <InputNumber
                          size="small"
                          min={0}
                          value={v}
                          onChange={(val) => fn_updateDetailCell(rec._index, 'unitPrice', val ?? 0)}
                        />
                      )
                    },
                    {
                      title: t('COL_AMOUNT', '금액'),
                      dataIndex: 'amount',
                      width: 140,
                      align: 'right',
                      render: (v: number) => (v ?? 0).toLocaleString()
                    },
                    {
                      title: t('COL_STATUS', '상태'),
                      dataIndex: '_state',
                      width: 70,
                      render: (s: string) => <RowStateTag state={s} />
                    },
                    {
                      title: t('COL_REMARK', '비고'),
                      dataIndex: 'remark',
                      render: (v, rec) => (
                        <Input
                          size="small"
                          value={v}
                          onChange={(e) =>
                            fn_updateDetailCell(rec._index, 'remark', e.target.value)
                          }
                        />
                      )
                    }
                  ]}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={6} align="right">
                        <strong>{t('LBL_TOTAL_AMT', '합계')}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right">
                        <strong>{calcTotal.toLocaleString()}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7} colSpan={3} />
                    </Table.Summary.Row>
                  )}
                />
              </>
            )
          },
          {
            key: 'items',
            label: '하위 품목',
            children: (
              <DataTable
                autoHeight={false}
                rowKey={(r) => `${r.lineNo}-${r.itemNo}`}
                dataSource={visibleItems.map((r) => r.data)}
                pagination={false}
                columns={[
                  { title: 'Line', dataIndex: 'lineNo', width: 60 },
                  { title: 'Item', dataIndex: 'itemNo', width: 60 },
                  { title: t('COL_ITEM_NAME', '품목명'), dataIndex: 'itemName' },
                  { title: t('COL_SPEC', '규격'), dataIndex: 'itemSpec' },
                  { title: t('COL_QTY', '수량'), dataIndex: 'itemQty', width: 100, align: 'right' }
                ]}
              />
            )
          }
        ]}
      />
    </PageContainer>
  )
}
